import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { orgSettingsService } from '@/services/orgSettings'
import { useAuthStore } from '@/stores/auth'

export const orgConsentEnforcementKeys = {
  all: (orgId?: string) => ['orgConsentEnforcement', orgId] as const,
  detail: (orgId?: string) =>
    [...orgConsentEnforcementKeys.all(orgId), 'detail'] as const,
}

export function useOrgConsentEnforcement() {
  const { organization } = useAuthStore()

  const { data, isLoading, error } = useQuery({
    queryKey: orgConsentEnforcementKeys.detail(organization?.id),
    queryFn: () => orgSettingsService.getConsentEnforcement(),
    enabled: !!organization?.id,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    enabled: data ?? false,
    isLoading,
    error,
  }
}

// Optimistic, with rollback to the prior value on error.
export function useUpdateOrgConsentEnforcement() {
  const queryClient = useQueryClient()
  const { organization } = useAuthStore()
  const detailKey = orgConsentEnforcementKeys.detail(organization?.id)

  return useMutation({
    mutationFn: (enabled: boolean) =>
      orgSettingsService.updateConsentEnforcement(enabled),
    onMutate: async (enabled: boolean) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous = queryClient.getQueryData<boolean>(detailKey)
      queryClient.setQueryData<boolean>(detailKey, enabled)
      return { previous }
    },
    onError: (_err, _enabled, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(detailKey, context.previous)
      }
      toast.error('Failed to update consent enforcement setting')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: orgConsentEnforcementKeys.all(organization?.id),
      })
    },
  })
}
